import { Color, MeshStandardMaterial, ShaderLib, UniformsUtils } from 'three';

import { materialEasyUniforms } from '@resn/gozer-three';

import { RipTransition } from '../Pack/transitions/rip';
import {
    shaderChunks as glslMatcap,
    uniforms as uniformsMatcap,
} from '../shaders/ShaderChunks/matcap';
import {
    shaderChunks as glslScene,
    uniforms as uniformsScene,
} from '../shaders/ShaderChunks/pack-scene';
import {
    shaderChunks as glslRainbow,
    uniforms as uniformsRainbow,
} from '../shaders/ShaderChunks/rainbow';
import {
    shaderChunks as glslBurn,
    uniforms as uniformsBurn,
} from '../shaders/ShaderChunks/transitions/burn';
import {
    defines as definesTransitionCommon,
    shaderChunks as glslTransitionCommon,
} from '../shaders/ShaderChunks/transitions/common';
import CrackTransition from '../shaders/ShaderChunks/transitions/crack';
import {
    shaderChunks as glslDissolve,
    uniforms as uniformsDissolve,
} from '../shaders/ShaderChunks/transitions/dissolve';
import {
    shaderChunks as glslShine,
    uniforms as uniformsShine,
} from '../shaders/ShaderChunks/transitions/shine';
import glslBlend from '@/libs/lygia/color/blend.glsl';
import glslLuma from '@/libs/lygia/color/luma.glsl';
import glslGamma2Linear from '@/libs/lygia/color/space/gamma2linear.glsl';
import glslLinear2Gamma from '@/libs/lygia/color/space/linear2gamma.glsl';
import glslFill from '@/libs/lygia/draw/fill.glsl';
import glslRect from '@/libs/lygia/draw/rect.glsl';
import glslAAstep from '@/libs/lygia/math/aastep.glsl';
import glslMap from '@/libs/lygia/math/map.glsl';
import glslSaturate from '@/libs/lygia/math/saturate.glsl';
import glslSum from '@/libs/lygia/math/sum.glsl';
import glslRectSDF from '@/libs/lygia/sdf/rectSDF.glsl';
import glslRotate from '@/libs/lygia/space/rotate.glsl';

class PackMaterial extends MeshStandardMaterial {
    constructor(props) {
        props = Object.assign(
            {
                type: props.type || 'PackMaterial',
                transparent: true,
                fog: true,
            },
            props
        );
        super(props);
        this.defines = {
            STANDARD: '',

            USE_UV: true,
            USE_ALPHAHASH: true,
            USE_TRANSMISSION: true,
            USE_FOG: true,
            USE_FOG_ALPHA: false,

            USE_RGB: true,
            USE_COLOR: true,
            // USE_SCENE: false,
            // USE_DEBUG: true,

            // set those in transitions
            // USE_TRANSITION_DISSOLVE: true,
            // USE_TRANSITION_BURN: true,
            // USE_TRANSITION_CRACK: true,
            // USE_TRANSITION_RIP: true,
            // USE_TRANSITION_SHINE: true,

            ...props.defines,
            ...definesTransitionCommon,
        };

        this.onBeforeCompile = (shader) => {
            shader.vertexShader = vertexShader;
            shader.fragmentShader = fragmentShader;
        };

        const uniforms = {
            ...uniformsMatcap(),
            ...uniformsRainbow(),
            ...uniformsDissolve(),
            ...uniformsBurn(),
            ...uniformsShine(),
            ...RipTransition.ripUniforms(),
            ...uniformsScene(),
            ...{
                tMatcap0: { value: null },
                tMatcap1: { value: null },
                tMatcapIrri: { value: null },
                tEdges: { value: null },
                tRGB: { value: null },
                tMainAsset: { value: null },

                fogNear: { value: -3 },
                fogFar: { value: -1 },

                u_shine: { value: 0 },
                u_transition_progress: { value: 0 },
                u_time: { value: 0 },
                u_color: { value: new Color() },

                u_burn_noiseScale: { value: 4.0 },

                USE_TRANSITION_DISSOLVE: { value: false },
                USE_TRANSITION_BURN: { value: false },
                USE_TRANSITION_CRACK: { value: false },
                USE_TRANSITION_RIP: { value: false },
                USE_TRANSITION_SHINE: { value: false },
                TRANSITION_MODE: { value: 0 },
                USE_SCENE: { value: false },
            },
        };
        // this.uniforms = uniforms;
        this.uniforms = UniformsUtils.merge([ShaderLib.standard.uniforms, uniforms]);

        materialEasyUniforms(this, uniforms);
    }

    clearTransitionUniforms({ reveal = false, rip = false } = {}) {
        if (reveal) {
            this.USE_TRANSITION_SHINE = false;
        }
        if (rip) {
            this.USE_TRANSITION_DISSOLVE = false;
            this.USE_TRANSITION_BURN = false;
            this.USE_TRANSITION_CRACK = false;
            this.USE_TRANSITION_RIP = false;

            this.TRANSITION_MODE = 1;
        }
        // this.needsUpdate = true;
    }
}
export default PackMaterial;

// const vertexShader = ShaderLib.standard.vertexShader;
const vertexShader = /* glsl */ `
#define STANDARD
varying vec3 vViewPosition;
varying vec3 vMeshPosition;
varying vec3 vTransformed;
varying vec4 vScreenPosition;
attribute float _direction;
#ifdef USE_TRANSMISSION
	varying vec3 vWorldPosition;
#endif
#include <common>
#include <batching_pars_vertex>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <color_pars_vertex>
#include <fog_pars_vertex>
#include <normal_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <shadowmap_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

uniform float u_transition_progress;
uniform float u_time;

uniform bool USE_TRANSITION_DISSOLVE;
uniform bool USE_TRANSITION_BURN;
uniform bool USE_TRANSITION_CRACK;
uniform bool USE_TRANSITION_RIP;
uniform bool USE_TRANSITION_SHINE;
uniform int TRANSITION_MODE;
uniform bool USE_SCENE;

${glslTransitionCommon.uniforms}
${glslBurn.uniforms}

${glslTransitionCommon.funcs}
${glslBurn.vertex}

${CrackTransition.uniforms}
${CrackTransition.vertexShader}

${RipTransition.uniforms}
${RipTransition.vertex}

${glslShine.uniforms}
${glslMap}

vec2 oval(float angle, vec2 radii) {
    return vec2(cos(angle) * radii.x, sin(angle) * radii.y);
}

void main() {
    vMeshPosition = position;
	#include <uv_vertex>
	#include <color_vertex>
	#include <morphinstance_vertex>
	#include <morphcolor_vertex>
	#include <batching_vertex>
	#include <beginnormal_vertex>
	#include <morphnormal_vertex>
	#include <skinbase_vertex>
	#include <skinnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>
	#include <begin_vertex>
    #include <morphtarget_vertex>

    if (USE_TRANSITION_BURN) {
        transformed = transition_burn_vertex(transformed, normal, u_transition_progress, u_time);
    }
    if (USE_TRANSITION_CRACK) {
        transformed = transition_crack_vertex(transformed, normal, u_transition_progress, u_time);
    }


    // Do peeling animation here

    if (USE_TRANSITION_RIP) {

        #ifdef IS_TOP
            // float peelx = mix(0.0, -1.0, smoothstep(0.0, 0.4, u_transition_progress));
            float peelx = mix(0.2, -3.0, u_transition_progress);
            transformed = applyRipAnimation(transformed, uPeelRotZ, peelx, uPeelPosY, uPeelRadius);
        #endif

        // #ifdef IS_RIP_LINE
        //     // transformed = applyRipAnimation(transformed, uPeelRotZ, uPeelPosX, uPeelPosY, uPeelRadius);
        // #endif

        #ifdef IS_BOTTOM
            // Create oval opening animation from top to bottom
            float progress = smoothstep(0.1, 0.5, u_transition_progress);
            
            // // Calculate falloff based on Y position - strongest at top, weakest at bottom
            float yFalloff = smoothstep(-0.4, 0.5, transformed.y); // 1.0 at top, 0.0 at bottom

            float radiusz = sin(transformed.x * 12.0) * 0.05 + 0.4;
            vec2 radii = vec2(1.0, radiusz) * 0.4;
            // bool backSide = bool(transformed.z > 0.0);
            float xNorm = map(transformed.x, -0.6, 0.6, 0.0, 1.0);
            vec2 pos;

            bool backSide = _direction > 0.0;
            bool frontSide = _direction < 0.0;

            if (backSide) {
                float angle = mix(3.14159265, 0.0, xNorm);
                pos = oval(angle, radii);
            } else if (frontSide) {
                float angle = mix(3.14159265, 6.2831853, xNorm);
                pos = oval(angle, radii);
            } else {
                pos = transformed.xz;
            }
            
            transformed.xz = mix(transformed.xz, pos, yFalloff * progress);
        #endif

    }

	#include <skinning_vertex>
	#include <displacementmap_vertex>
	#include <project_vertex>
	#include <logdepthbuf_vertex>
	#include <clipping_planes_vertex>
	vViewPosition = - mvPosition.xyz;
	#include <worldpos_vertex>
    
	#include <shadowmap_vertex>
	#include <fog_vertex>
    // #ifdef USE_TRANSMISSION
    vWorldPosition = worldPosition.xyz;
    vTransformed = transformed;
    vScreenPosition = gl_Position;
    // #endif
}
 `;

const fragmentShader = /* glsl */ `
    #include <common>
    #include <map_pars_fragment>
    #include <normal_pars_fragment>
    #include <uv_pars_fragment>
    #include <bumpmap_pars_fragment>
    #include <normalmap_pars_fragment>

    varying vec3 vTransformed;
    varying vec3 vViewPosition;
    varying vec3 vWorldPosition;
    varying vec3 vMeshPosition;
    varying vec4 vScreenPosition;

    uniform sampler2D tRGB;
    uniform sampler2D tMatcap0;
    uniform sampler2D tMatcap1;
    uniform sampler2D tMatcapIrri;
    uniform sampler2D tEdges;
    uniform sampler2D tMainAsset;

    uniform vec3 u_color;
    uniform float u_transition_progress;
    uniform float u_time;
    
    #ifdef USE_FOG
        varying float vFogDepth;
        uniform float fogNear;
        uniform float fogFar;
    #endif

    uniform bool USE_TRANSITION_DISSOLVE;
    uniform bool USE_TRANSITION_BURN;
    uniform bool USE_TRANSITION_CRACK;
    uniform bool USE_TRANSITION_RIP;
    uniform bool USE_TRANSITION_SHINE;
    uniform int TRANSITION_MODE;
    uniform bool USE_SCENE;
    
    ${glslBlend}
    ${glslRotate}
    ${glslLuma}
    ${glslSum}
    ${glslSaturate}
    ${glslAAstep}
    ${glslRectSDF}
    ${glslRect}
    ${glslFill}
    ${glslGamma2Linear}
    ${glslLinear2Gamma}

    ${glslTransitionCommon.uniforms}
    ${glslMatcap.uniforms}
    ${glslRainbow.uniforms}
    ${glslDissolve.uniforms}
    ${glslBurn.uniforms}
    ${CrackTransition.uniforms}
    ${RipTransition.uniforms}
    ${glslShine.uniforms}
    ${glslScene.uniforms}

    ${glslTransitionCommon.funcs}
    ${glslRainbow.funcs}
    ${glslMatcap.funcs}
    ${glslDissolve.funcs}
    ${glslBurn.funcs}
    ${CrackTransition.fragmentShader}
    ${RipTransition.fragment}
    ${glslShine.funcs}
    ${glslScene.funcs}
    
    float map(in float value, in float inMin, in float inMax, in float outMin, in float outMax) {
        return outMin + (outMax - outMin) * (value - inMin) / (inMax - inMin);
    }
    
    void main() {
        #include <normal_fragment_begin>
        #include <normal_fragment_maps>
        
        vec3 viewPosition = vViewPosition;
        vec3 viewDir = normalize(viewPosition); // view direction in view space

        vec3 mvPosition = vViewPosition;
        mvPosition = rotate(mvPosition, u_matcapRotationWorld, vec3(0.0, 1.0, 0.0));
        if (USE_TRANSITION_RIP) {
            // normal =  transition_rip_normal_fragment(vWorldPosition, vMeshPosition, normal, vWorldPosition.y-0.7, u_time);
            // color.a = ripResult.a;
        }

        // #ifdef USE_BUMPMAP
        // normal = perturbNormalArb( - vViewPosition, normal, dHdxy_fwd(), faceDirection );
        // #endif

        vec4 color = vec4(vec3(0.0), 1.0);

        // RGB channel to define materials
        #ifdef USE_RGB
            vec3 cRGB = texture2D(tRGB, vUv).rgb;
        #endif

        // ― Matcap Base
        #ifdef USE_MATCAP_BASE
            vec3 cMatcap = matcap(tMatcap, normal, mvPosition, false, 1.0, 1.0, color.a, u_matcapRotation);
            color.rgb = cMatcap;
        #endif
        
        // ― Colorizing
        #ifdef USE_COLOR
            float mC = 1.0;
            #ifdef USE_RGB
                mC = sum(cRGB);
            #endif
            color.rgb = blendMultiply(color.rgb, u_color, 1.);
            // color.rgb = blendColor(color.rgb, u_color, 1.);
            // color.rgb = u_color;
        #endif

        
        
        // ― Irridescent is now in the 3d scene
        // ― Matcap Irridescent
        #ifdef USE_MATCAP_IRRI
            float mMcI = 1.0;
            #ifdef USE_RGB
                mMcI = cRGB.r;
            #endif
            vec3 cMatcapIrri = matcap(tMatcapIrri, normal, mvPosition, false, 0.0, 1.0, color.a, u_matcapRotation, u_matcapScale);
            // color.rgb = blendColorDodge(color.rgb, cMatcapIrri, mMcI);
            // color.rgb = blendScreen(color.rgb, cMatcapIrri, mMcI);
            // Use this one below

            #ifdef IS_TOP
                color.rgb = mix(color.rgb, cMatcapIrri, mMcI);
            #endif

            #ifdef IS_BOTTOM
                // if (!USE_SCENE) {
                color.rgb = mix(color.rgb, cMatcapIrri, mMcI);
                // }
            #endif

        #endif

        // ― Scene
        if (USE_SCENE) {
            float rectSdf = rectSDF(vUv - uRectOffset, uRectSize);
            float sceneMask = fill(rectSdf, 1.0, 0.2);
            vec4 cScene = getSceneColor(tScene, vScreenPosition);
            // vec3 bg = vec3(5./255., 15./255., 20./255.);
            // vec3 sceneOutput = mix(bg, cScene.rgb, cScene.a);
            color.rgb = mix(color.rgb, cScene.rgb, cScene.a * 0.9 * sceneMask);
            // color.rgb = cScene.rgb;
            // color.rgb = vec3(sceneMask);u
            // color.rgb = vec3(step(0.25, vUv.y)) * vec3(step(vUv.y, 0.9));
            // color.rgb *= vec3(step(0.4, vUv.x)) * vec3(step(vUv.x, 0.8));
        }

        // ― Matcap Shiny
        #ifdef USE_MATCAP_0
            float mMC0 = 1.0;
            #ifdef USE_RGB
                mMC0 = cRGB.b;
            #endif
            vec3 cMatcap0 = matcap(tMatcap0, normal, mvPosition, false, 0.0, 1.0, color.a, 0.0);
            color.rgb = mix(color.rgb, cMatcap0, mMC0);
            // color.rgb = blendMultiply(color.rgb, vec3(1.0), mMC0);
            // color.rgb = blendScreen(color.rgb, vec3(1.0), mMC0);
        #endif

        // ― Matcap Matte 1
        // matcapPackBlack
        #ifdef USE_MATCAP_1
            float mMC1 = 1.0;
            #ifdef USE_RGB
                mMC1 = cRGB.g;
            #endif
            vec3 cMatcap1 = matcap(tMatcap1, normal, mvPosition, false, 0.0, 1.0, color.a, u_matcapRotation);
            color.rgb = mix(color.rgb, cMatcap1, mMC1);
        #endif

        // ― Rainbow 
        #ifdef USE_RAINBOW
            vec3 cRainbow = rainbow(viewDir, normal, u_rwThickness, u_rwMovement, u_rwWavelengths, u_rwNoiseStrength, u_rwNoiseScale, u_rwOffset, false, 1.0, 1.0, accumAlpha);
            float mRw = 1.0;
            #ifdef USE_RGB
                mRw = cRGB.r;
            #endif
            color.rgb = blendOverlay(color.rgb, cRainbow, mRw);
        #endif

        // ― Rip Line
        #ifdef IS_RIP_LINE
            // vec3 baseRipColor = vec3(0.3, 0.3, 0.3);
            vec3 baseRipColor = vec3(100.0 / 255.0, 123.0 / 255.0, 172.0 / 255.0);

            // ― Matcap Irridescent
            #ifdef USE_MATCAP_IRRI
                color.rgb = blendColorDodge(baseRipColor, cMatcapIrri, 1.0);
                // color.rgb = cMatcapIrri;
            #endif
        #endif

        // ― Transitions
        if (USE_TRANSITION_DISSOLVE) {
            vec2 stDissolve = scale(vTransformed.xy * .5 + .5, 1.5);
            vec4 resDissolve = dissolve(tNoiseDissolve, stDissolve, 1. - u_transition_progress, u_color_dissolve, u_dissolve_offset);
            
            color.rgb = blendHardLight(color.rgb, resDissolve.rgb, smoothstep(0., 0.2, u_transition_progress));
            color.a = resDissolve.a;
        }

        if (USE_TRANSITION_BURN) {
            vec4 burnResult = transition_burn(vTransformed, normal, u_transition_progress, u_time, true);
            // color.rgb = mix(color.rgb, burnResult.rgb, max(0.0, (burnResult.a-0.5) * min(1.0, u_transition_progress*100.0)));
            color.rgb += burnResult.rgb;
            color.a = burnResult.a;
        }

        if (USE_TRANSITION_CRACK) {
            vec4 crackResult = transition_crack_fragment(vPositionCrack, normal, u_transition_progress, u_time);
            color.rgb = color.rgb +  crackResult.rgb;
            color.a = crackResult.a;
        }

        if (USE_TRANSITION_SHINE) {
            color.rgb = transition_shine(color.rgb, vWorldPosition, u_shine, u_shine_angle);
        }

        if (USE_TRANSITION_RIP) {
            // vec4 ripResult = transition_rip_fragment(vWorldPosition, vMeshPosition, normal, u_transition_progress, u_time);
            // color.rgb = mix(color.rgb,  ripResult.rgb, ripResult.a);            
            // color.rgb *= 1.0 - min(1.0, max(0.0, -vWorldPosition.y-0.5) * 2.0);
            // color.a = ripResult.a;

            // #ifdef IS_TOP
            // #endif

            #ifdef IS_RIP_LINE
                float spread = 0.1;
                float pr = map(u_transition_progress, 0.0, 0.3, 0.0, 1.0);
                float threshold = mix(-0.5, 0.5, pr);

                float alpha = smoothstep(threshold - spread, threshold, vTransformed.x);
                color.a *= alpha;
            #endif

            // #ifdef IS_BOTTOM
            // #endif
        }



        gl_FragColor = color;
        // gl_FragColor = texture2D(bumpMap, vUv);
        // gl_FragColor = vec4(normal, 1.0);

        #ifdef USE_FOG
            float fogFactor = smoothstep(fogNear, fogFar, vWorldPosition.z);
            gl_FragColor.rgb *= fogFactor;
        #endif
        #ifdef USE_FOG_ALPHA 
            float fogFactorA = smoothstep(fogNear, fogFar, vWorldPosition.z);
            gl_FragColor.a *= fogFactorA;
        #endif

        // use this part for debugging
        /* #if defined(USE_DEBUG) && defined(USE_BUMPMAP)
            gl_FragColor.rgb = vec3(bumpScale * texture2D( bumpMap, vBumpMapUv ).r);
        #endif */
/*         #if defined(USE_DEBUG) && defined(USE_DISSOLVE)
            vec2 ccc = scale(vTransformed.y * .5 + .5, 0.675);
            gl_FragColor.rgb = vec3(step(0.5, ccc.y));
        #endif */
        #if defined(USE_DEBUG)
            // #ifdef USE_MATCAP_0
            //     gl_FragColor.rgb = cMatcap0;
            // #endif
            gl_FragColor.rgb = vec3(1.0, 0.0, 0.0);
            gl_FragColor.a = 1.0;
        #endif
    }
`;
